import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { object } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@mui/material';

import EmailDisplay from 'components/EmailDisplay';
import ErrorPanel from 'components/ErrorPanel';
import SuccessPanel from 'components/SuccessPanel';
import { personalInfoValidationSchema } from 'validationSchemas';
import { clearError, completeUser } from 'store/user/actions';
import { errorSelector, isLoadingSelector } from 'store/user/selectors';
import { LOGIN_PASSWORD, PERSON } from 'assets/Icons';

import '../sign-up-form.scss';

const PersonalInfoSubForm = (props) => {
  const { jwtData } = props;
  const dispatch = useDispatch();
  const isLoading = useSelector(isLoadingSelector);
  const serverError = useSelector(errorSelector);
  const [showEmailVerificationMessage, setShowEmailVerificationMessage] = useState(true);
  const [showError, setShowError] = useState(true);
  const [unitedErrors, setUnitedErrors] = useState([]);
  const { values, touched, handleChange, handleSubmit, setFieldTouched, validateForm } = useFormik({
    initialValues: {
      firstName: jwtData?.given_name || '',
      lastName: jwtData?.family_name || '',
      password: '',
      passwordConfirmation: '',
      iss: jwtData?.iss || 'userpass',
    },
    onSubmit: (submittedValues) => {
      dispatch(completeUser(submittedValues));
    },
    validationSchema: personalInfoValidationSchema,
  });

  useEffect(() => {
    setShowError(true);
  }, [serverError]);

  const closeEmailVerificationMessage = () => {
    setShowEmailVerificationMessage(false);
  };

  const closeErrorPanel = () => {
    if (serverError) dispatch(clearError());
    setShowError(false);
    setUnitedErrors([]);
  };

  const handleInputChange = (event) => {
    if (showEmailVerificationMessage) closeEmailVerificationMessage();
    if (showError) closeErrorPanel();
    else if (serverError) dispatch(clearError());
    handleChange(event);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = await validateForm();
    if (Object.keys(nextErrors).length > 0) {
      const nextUnitedErrors = [serverError];
      Object.keys(nextErrors).forEach((error) => {
        if (touched[error]) {
          nextUnitedErrors.push(nextErrors[error]);
        }
      });
      setShowError(true);
      setUnitedErrors(nextUnitedErrors);
      return;
    }
    handleSubmit();
  };

  return (
    <form onSubmit={onSubmit} className="form-container">
      {showEmailVerificationMessage && (
        <>
          <SuccessPanel
            message="Your email has been successfully verified"
            buttonClickHandler={closeEmailVerificationMessage}
          />
          <EmailDisplay />
        </>
      )}
      {serverError ? (
        <ErrorPanel
          message={serverError}
          buttonClickHandler={closeErrorPanel}
          key={serverError}
          dataTestId="signup-personal-server-error"
        />
      ) : (
        unitedErrors.map((error, index) =>
          error ? (
            <ErrorPanel
              message={error}
              buttonClickHandler={closeErrorPanel}
              key={error}
              dataTestId={`signup-personal-validation-error-${index}`}
            />
          ) : null,
        )
      )}
      <div className="input-container">
        <div className="icon-container">
          <img src={PERSON} className="person-icon" alt="person-icon" />
        </div>
        <input
          value={values.firstName}
          name="firstName"
          placeholder="First Name"
          required
          onChange={handleInputChange}
          onBlur={() => setFieldTouched('firstName')}
        />
      </div>
      <div className="input-container">
        <div className="icon-container">
          <img src={PERSON} className="person-icon" alt="person-icon" />
        </div>
        <input
          value={values.lastName}
          name="lastName"
          placeholder="Last Name"
          required
          onChange={handleInputChange}
          onBlur={() => setFieldTouched('lastName')}
        />
      </div>
      {(!jwtData || jwtData.iss === 'userpass') && (
        <>
          <div className="input-container">
            <div className="icon-container">
              <img src={LOGIN_PASSWORD} className="person-icon" alt="person-icon" />
            </div>
            <input
              type="password"
              value={values.password}
              name="password"
              placeholder="Password"
              required
              onChange={handleInputChange}
              onBlur={() => setFieldTouched('password')}
            />
          </div>
          <div className="input-container">
            <div className="icon-container">
              <img src={LOGIN_PASSWORD} className="person-icon" alt="person-icon" />
            </div>
            <input
              type="password"
              value={values.passwordConfirmation}
              name="passwordConfirmation"
              placeholder="Confirm Your Password"
              required
              onChange={handleInputChange}
              onBlur={() => setFieldTouched('passwordConfirmation')}
            />
          </div>
        </>
      )}
      <div className="submit-button-container">
        <Button fullWidth sx={{ fontSize: 18, p: 2 }} type="submit" variant="contained" disabled={isLoading}>
          Next
        </Button>
      </div>
    </form>
  );
};

PersonalInfoSubForm.propTypes = {
  jwtData: object,
};

export default PersonalInfoSubForm;
